import { Link } from "react-router";

import { DataStorageNotice } from "@/components/data-storage-notice";
import { Eyebrow } from "@/components/eyebrow";
import { Card, CardContent } from "@/components/ui/card";

interface ResultsGateProps {
  /** Return to the review step without submitting. */
  onBack: () => void;
}

// The wizard lives at /assess, so the sign-in / sign-up routes carry this back
// as their returnTo; once authenticated the persisted "submit-pending" draft
// then auto-unlocks.
const RETURN_TO = "/assess";

/**
 * Sign-in wall shown when an anonymous user finishes the wizard and asks for
 * their report.
 *
 * Nothing has been computed yet. The wizard holds only the answers the user
 * typed; scoring and report generation run server-side in `engine/`, behind
 * `POST /assessments`, which requires a session. Signing in is what lets that
 * request be made at all, so there is no report sitting in browser state for
 * anyone to lift out of dev tools, and the gate is a real one rather than a
 * curtain drawn over a result the client already holds.
 */
export function ResultsGate({ onBack }: ResultsGateProps) {
  return (
    <div>
      <Eyebrow>Almost there</Eyebrow>
      <h1 className="text-primary mt-3 max-w-[20ch] text-4xl font-normal tracking-tight sm:text-5xl">
        Your report is ready. <span className="text-accent">Sign in to unlock it.</span>
      </h1>
      <p className="text-muted-foreground mt-4 mb-8 text-lg">
        Sign in to save this report to your account and open it.
      </p>

      <Card className="border-accent">
        <CardContent className="space-y-5 pt-6">
          <div className="text-muted-foreground space-y-2 text-center text-sm">
            <p>
              <Link
                to={`/login?returnTo=${encodeURIComponent(RETURN_TO)}`}
                className="text-accent font-medium hover:underline"
              >
                Sign in with email
              </Link>
            </p>
            <p>
              New to the index?{" "}
              <Link
                to={`/signup?returnTo=${encodeURIComponent(RETURN_TO)}`}
                className="text-accent font-medium hover:underline"
              >
                Create an account
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>

      <p className="text-muted mt-4 text-xs">
        <DataStorageNotice />
      </p>

      <div className="mt-8">
        <button
          type="button"
          onClick={onBack}
          className="text-muted hover:text-primary text-sm transition-colors"
        >
          ← Back to review
        </button>
      </div>
    </div>
  );
}
